import { Button, Card } from "@rumbledirect/ui";
import { message, Popconfirm, Space } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  deleteDocumentLibraryById,
  fetchDocumentLibraryItems,
  deleteDocumentFromLibrary,
} from "../../../lib/api/resources/documentLibraries";
import { UploadDocumentModal } from "./components/UploadDocumentModal";
import { EditDocumentCompanyModal } from "./components/EditDocumentCompanyModal";
import { documentLibraryItemColumns } from "./lib/constants/documentLibraryItemColumns";
import { Table } from "@/components/v2";
import { DocumentLibraryItem } from "@rumble-it/common-lib/types/models/DocumentLibraryItem.interface";

export const DocumentLibraryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editCompanyDocument, setEditCompanyDocument] =
    useState<DocumentLibraryItem | null>(null);

  if (!id) return null;

  const [page, setPage] = useState(1);

  const documentItemsQuery = useQuery({
    queryKey: ["documentLibraryItems", id],
    queryFn: () => fetchDocumentLibraryItems(id, page),
  });

  const deleteLibraryMutation = useMutation({
    mutationFn: () => deleteDocumentLibraryById(id),
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: ["documentLibraryItems"],
      });
      navigate("/document-libraries");
      message.success("Bibliotheek succesvol verwijderd");
    },
    onError: () =>
      message.error(
        "Er is een fout opgetreden bij het verwijderen van de bibliotheek",
      ),
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: (documentId: string) =>
      deleteDocumentFromLibrary(id, documentId),
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: ["documentLibraryItems"],
      });
      message.success("Document succesvol verwijderd");
    },
    onError: () =>
      message.error(
        "Er is een fout opgetreden bij het verwijderen van het document",
      ),
  });

  return (
    <div>
      <Card
        title="Documenten"
        extra={
          <Space>
            <Button onClick={() => setUploadModalOpen(true)}>
              Upload document
            </Button>
            <Popconfirm
              title="Bibliotheek verwijderen"
              description="Weet je zeker dat je deze bibliotheek wilt verwijderen?"
              onConfirm={() => deleteLibraryMutation.mutate()}
              okText="Ja"
              cancelText="Nee"
            >
              <Button danger loading={deleteLibraryMutation.isPending}>
                Verwijder bibliotheek
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        <Table
          columns={documentLibraryItemColumns(
            deleteDocumentMutation.mutate,
            setEditCompanyDocument,
          )}
          dataSource={documentItemsQuery.data?.member}
          loading={
            documentItemsQuery.isFetching || deleteDocumentMutation.isPending
          }
          pagination={{
            pageSize: 30,
            total: documentItemsQuery.data?.totalItems,
            showSizeChanger: false,
            onChange: (page) => setPage(page),
          }}
          rowKey="id"
        />
      </Card>

      <UploadDocumentModal
        open={uploadModalOpen}
        setOpen={setUploadModalOpen}
        libraryId={id}
      />

      <EditDocumentCompanyModal
        open={editCompanyDocument !== null}
        setOpen={(open) => {
          if (!open) setEditCompanyDocument(null);
        }}
        libraryId={id}
        document={editCompanyDocument}
      />
    </div>
  );
};
